import { useEffect } from "react";
import { cardStyle } from "./styles";
import { useIsMobile } from "../lib/useIsMobile";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): React.JSX.Element | null {
  const isMobile = useIsMobile();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !busy) onCancel();
      if (e.key === "Enter" && !busy) onConfirm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel, onConfirm]);

  if (!open) return null;

  const confirmBg = danger
    ? "linear-gradient(135deg,#FF4D4F,#B30000)"
    : "linear-gradient(135deg,#FFD500,#FFA500)";
  const confirmShadow = danger
    ? "0 4px 16px rgba(255,77,79,.35)"
    : "0 4px 16px rgba(255,213,0,.30)";
  const confirmFg = danger ? "#fff" : "#000";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ql-confirm-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(6,8,14,.66)",
        backdropFilter: "blur(8px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        animation: "ql-fade-in .15s var(--ql-ease)",
      }}
    >
      <div
        style={{
          ...cardStyle,
          maxWidth: "min(460px, calc(100vw - 24px))",
          width: "100%",
          padding: isMobile ? 16 : 28,
          boxShadow: "0 24px 60px rgba(0,0,0,.55), 0 2px 0 rgba(255,255,255,.04) inset",
          animation: "ql-pop-in .18s var(--ql-ease)",
        }}
      >
        <div
          id="ql-confirm-title"
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: "var(--ql-fg)",
            marginBottom: 10,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 14,
            lineHeight: 1.55,
            color: "var(--ql-fg-muted)",
            marginBottom: 24,
          }}
        >
          {message}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: "10px 20px",
              borderRadius: 12,
              fontWeight: 600,
              fontSize: 13,
              cursor: busy ? "not-allowed" : "pointer",
              fontFamily: "inherit",
              border: "1px solid rgba(255,255,255,.10)",
              background: "rgba(255,255,255,.03)",
              color: "var(--ql-fg-muted)",
              transition: "all .15s var(--ql-ease)",
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            autoFocus
            style={{
              padding: "10px 22px",
              borderRadius: 12,
              fontWeight: 700,
              fontSize: 13,
              cursor: busy ? "wait" : "pointer",
              fontFamily: "inherit",
              border: "none",
              color: confirmFg,
              background: confirmBg,
              boxShadow: confirmShadow,
              opacity: busy ? 0.7 : 1,
              transition: "all .15s var(--ql-ease)",
            }}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
